import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { History, RotateCcw, GitCompare } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import Modal, { btnSecondary, btnPrimary } from "@/components/Modal";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmDialog";
import { fmtDateTime } from "@/lib/constants";

export default function FormulaHistory({ formulaId, onRestored }) {
  const { canWrite } = useAuth();
  const confirm = useConfirm();
  const [view, setView] = useState(null); // {mode:'view'|'diff', version}
  const [restoring, setRestoring] = useState(false);

  const { data: versions = [], isLoading, refetch } = useQuery({
    queryKey: ["formula-versions", formulaId],
    queryFn: async () => (await api.get(`/formulas/${formulaId}/versions`)).data,
  });

  const { data: diff } = useQuery({
    queryKey: ["formula-diff", formulaId, view?.version?.id],
    queryFn: async () => (await api.get(`/formulas/${formulaId}/versions/${view.version.id}/diff`)).data,
    enabled: view?.mode === "diff",
  });

  const restore = async (versionId) => {
    const ok = await confirm({
      title: "Restore version",
      message: "Restore this version? A new version will be created on top — history is never overwritten.",
      confirmLabel: "Restore version",
      danger: false,
    });
    if (!ok) return;
    setRestoring(true);
    try {
      const res = await api.post(`/formulas/${formulaId}/versions/${versionId}/restore`);
      toast.success(`Restored v${res.data.restored_from} as v${res.data.new_version}`);
      setView(null);
      refetch();
      onRestored?.();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="bg-white border border-[#E8DFD1] rounded-xl shadow-sm overflow-hidden" data-testid="formula-history-card">
      <div className="flex items-center gap-2 px-6 py-4 border-b border-[#F4EFEA]">
        <History size={17} className="text-[#C5A059]" />
        <h2 className="font-display text-lg font-semibold text-[#1C100B]">Version History ({versions.length})</h2>
      </div>

      <table className="w-full text-sm">
        <thead className="bg-[#FAF6F0] text-[#2A1810] text-xs uppercase tracking-wider">
          <tr>
            <th className="px-4 py-3 text-left font-semibold">Version</th>
            <th className="px-4 py-3 text-left font-semibold">Date</th>
            <th className="px-4 py-3 text-left font-semibold">Author</th>
            <th className="px-4 py-3 text-left font-semibold">Change note</th>
            <th className="px-4 py-3 text-right font-semibold">Lines</th>
            <th className="px-4 py-3" />
          </tr>
        </thead>
        <tbody>
          {isLoading && <tr><td colSpan={6} className="px-4 py-8 text-center text-[#8C7A6B]">Loading…</td></tr>}
          {!isLoading && versions.length === 0 && (
            <tr>
              <td colSpan={6} className="px-4 py-8 text-center text-[#8C7A6B]" data-testid="history-empty">
                No snapshots yet. The next edit to this formula will create one.
              </td>
            </tr>
          )}
          {versions.map((v) => (
            <tr key={v.id} className="border-b border-[#F4EFEA] last:border-0" data-testid={`version-row-${v.version}`}>
              <td className="px-4 py-3 font-bold text-[#2A1810]">v{v.version}</td>
              <td className="px-4 py-3 text-[#8C7A6B] whitespace-nowrap">{fmtDateTime(v.created_at)}</td>
              <td className="px-4 py-3 text-[#5C4A3E]">{v.created_by_name || "—"}</td>
              <td className="px-4 py-3 text-[#5C4A3E] max-w-sm"><span className="line-clamp-2">{v.change_note || "—"}</span></td>
              <td className="px-4 py-3 text-right text-[#5C4A3E]">{v.lines?.length ?? 0}</td>
              <td className="px-4 py-3">
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setView({ mode: "view", version: v })}
                    className="px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-[#E8DFD1] bg-white text-[#2A1810] hover:border-[#C5A059]"
                    data-testid={`view-version-${v.version}`}
                  >
                    View
                  </button>
                  <button
                    onClick={() => setView({ mode: "diff", version: v })}
                    className="px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-[#E8DFD1] bg-white text-[#2A1810] hover:border-[#C5A059] inline-flex items-center gap-1"
                    data-testid={`diff-version-${v.version}`}
                  >
                    <GitCompare size={12} /> Diff
                  </button>
                  {canWrite && (
                    <button
                      onClick={() => restore(v.id)}
                      disabled={restoring}
                      className="px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-[#E5C992] bg-[#F9F3E6] text-[#8A6D2F] hover:bg-[#F4EBD7] inline-flex items-center gap-1 disabled:opacity-50"
                      data-testid={`restore-version-${v.version}`}
                    >
                      <RotateCcw size={12} /> Restore
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {view?.mode === "view" && (
        <Modal title={`Version v${view.version.version} (read-only)`} onClose={() => setView(null)} testid="version-view-modal">
          <div className="space-y-4" data-testid="version-snapshot">
            <div className="grid grid-cols-2 gap-4 text-xs">
              <div><span className="block text-[#8C7A6B] uppercase tracking-wider font-semibold mb-1">Name</span><span className="text-[#2A1810] font-medium">{view.version.formula?.name}</span></div>
              <div><span className="block text-[#8C7A6B] uppercase tracking-wider font-semibold mb-1">Status</span><span className="text-[#2A1810] font-medium">{view.version.formula?.status}</span></div>
              <div><span className="block text-[#8C7A6B] uppercase tracking-wider font-semibold mb-1">Category</span><span className="text-[#2A1810] font-medium">{view.version.formula?.category}</span></div>
              <div><span className="block text-[#8C7A6B] uppercase tracking-wider font-semibold mb-1">Author</span><span className="text-[#2A1810] font-medium">{view.version.created_by_name}</span></div>
            </div>
            <p className="text-sm text-[#5C4A3E]">{view.version.formula?.description || "No description."}</p>
            <table className="w-full text-sm border border-[#F4EFEA] rounded-lg overflow-hidden">
              <thead className="bg-[#FAF6F0] text-[#2A1810] text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Ingredient</th>
                  <th className="px-3 py-2 text-right font-semibold">%</th>
                  <th className="px-3 py-2 text-right font-semibold">Yield</th>
                </tr>
              </thead>
              <tbody>
                {(view.version.lines || []).map((l, i) => (
                  <tr key={i} className="border-t border-[#F4EFEA]">
                    <td className="px-3 py-2 text-[#2A1810]">{l.ingredient_name}</td>
                    <td className="px-3 py-2 text-right text-[#5C4A3E]">{l.percentage}%</td>
                    <td className="px-3 py-2 text-right text-[#5C4A3E]">{l.yield_factor}</td>
                  </tr>
                ))}
                {!view.version.lines?.length && (
                  <tr><td colSpan={3} className="px-3 py-4 text-center text-[#8C7A6B] text-xs">No lines in this snapshot.</td></tr>
                )}
              </tbody>
            </table>
            {canWrite && (
              <div className="flex justify-end gap-2 pt-2 border-t border-[#F4EFEA]">
                <button onClick={() => setView(null)} className={btnSecondary}>Close</button>
                <button onClick={() => restore(view.version.id)} className={btnPrimary} data-testid="modal-restore-button">
                  <RotateCcw size={14} /> Restore this version
                </button>
              </div>
            )}
          </div>
        </Modal>
      )}

      {view?.mode === "diff" && (
        <Modal title={`v${view.version.version} vs current`} onClose={() => setView(null)} testid="version-diff-modal">
          {!diff ? (
            <div className="text-sm text-[#8C7A6B]">Computing diff…</div>
          ) : (
            <div className="space-y-5" data-testid="version-diff">
              <DiffGroup label="Fields changed" testid="diff-fields" empty="No field changes.">
                {diff.fields.map((f) => (
                  <div key={f.field} className="text-sm flex flex-wrap items-center gap-2">
                    <span className="text-[#8C7A6B] uppercase text-[10px] font-bold tracking-wider">{f.field}</span>
                    <span className="bg-[#FEF2F2] text-[#B91C1C] px-2 py-0.5 rounded line-through">{String(f.from ?? "—")}</span>
                    <span className="text-[#8C7A6B]">→</span>
                    <span className="bg-[#ECFDF5] text-[#065F46] px-2 py-0.5 rounded font-medium">{String(f.to ?? "—")}</span>
                  </div>
                ))}
              </DiffGroup>
              <DiffGroup label="Lines added since this version" testid="diff-added" empty="None.">
                {diff.added.map((l, i) => (
                  <div key={i} className="text-sm bg-[#ECFDF5] text-[#065F46] border border-[#A7F3D0] rounded px-3 py-1.5">
                    + {l.ingredient_name} — {l.percentage}%
                  </div>
                ))}
              </DiffGroup>
              <DiffGroup label="Lines removed since this version" testid="diff-removed" empty="None.">
                {diff.removed.map((l, i) => (
                  <div key={i} className="text-sm bg-[#FEF2F2] text-[#B91C1C] border border-[#FECACA] rounded px-3 py-1.5">
                    − {l.ingredient_name} — {l.percentage}%
                  </div>
                ))}
              </DiffGroup>
              <DiffGroup label="Percentages changed" testid="diff-changed" empty="None.">
                {diff.changed.map((l, i) => (
                  <div key={i} className="text-sm bg-[#F9F3E6] border border-[#E5C992] rounded px-3 py-1.5 text-[#8A6D2F]">
                    {l.ingredient_name}: <span className="line-through">{l.from}%</span> → <span className="font-bold">{l.to}%</span>
                  </div>
                ))}
              </DiffGroup>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

function DiffGroup({ label, children, empty, testid }) {
  const items = Array.isArray(children) ? children.filter(Boolean) : children ? [children] : [];
  return (
    <div data-testid={testid}>
      <h4 className="text-xs font-semibold tracking-wider uppercase text-[#8C7A6B] mb-2">{label}</h4>
      {items.length ? <div className="space-y-1.5">{items}</div> : <p className="text-sm text-[#8C7A6B]">{empty}</p>}
    </div>
  );
}
